import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Button } from '@zocdoc-frontend-common/components-button';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    withCustomMetrics,
    INTERACTION_TYPES,
    EVENT_INITIATORS,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { blue100, yellow60 } from '@zocdoc-mezzanine/foundations-colors';
import { NewOrExistingPatient } from '../../components';

const useMountEffect = (fun) => useEffect(fun, []);

export const NewOrExistingVerification = ({
    isNewPatient,
    professionalName,
    onClickContinue,
    onRadioChange,
    sendMetric,
}) => {
    // We only show a prefilled checkbox if the user goes BACK to this step in the browser
    // Therefore this must be null on first view (controlled by parent)
    // If this experiment is merged, this logic should be cleaned into one place
    const [canContinue, setCanContinue] = useState(isNewPatient !== null);
    const [hasError, setHasError] = useState(false);

    const handleRadioChange = (value) => {
        setCanContinue(true);
        const newPatient = value === 'newPatient';
        sendMetric({
            element: newPatient ? 'NewPatient' : 'ExistingPatient',
            action: `Clicked ${
                newPatient ? 'New Patient' : 'Existing Patient'
            }`,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        if (hasError) {
            setHasError(false);
        }
        onRadioChange(value);
    };

    const handleOnClick = (e) => {
        sendMetric({
            interactionType: INTERACTION_TYPES.CLICK,
            element: 'ContinueInterim',
            component: 'NewExistingInterimCTA',
            action: 'Clicked Continue',
        });
        if (canContinue) {
            onClickContinue(e);
        } else {
            sendMetric({
                interactionType: INTERACTION_TYPES.VIEW,
                element: 'ContinueInterimError',
                component: 'NewExistingInterimCTA',
                action: 'Clicked Continue Error',
            });
            setHasError(true);
        }
    };

    useMountEffect(() => {
        sendMetric({
            action: 'Viewed New Existing Interim Step',
            interactionType: INTERACTION_TYPES.VIEW,
        });
    });

    // If seeing this verification, radios should be EMPTY until selection is made
    // isNewPatient value thus must be null on first view
    return (
        <div data-test="new-existing-verification-step">
            <Label>{`Have you seen ${professionalName} before?`}</Label>
            <NewOrExistingPatient
                alwaysShowVertical
                hasError={hasError}
                isNewPatient={isNewPatient}
                showError={hasError}
                onRadioChange={handleRadioChange}
            />
            {hasError && (
                <ErrorText data-test="continue-interim-error">
                    Please select whether you've seen this doctor
                </ErrorText>
            )}
            <ContinueButton
                data-test="continue-interim-button"
                data-uem-id="continue-interim-button"
                onClick={handleOnClick}
                hasError={hasError}
            >
                Continue
            </ContinueButton>
        </div>
    );
};

const Label = styled.div`
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 15px;
    line-height: 20px;
    padding-bottom: 9px;
    ${breakpoint.medium} {
        margin-top: 40px;
        font-size: 18px;
        line-height: 24px;
        padding-bottom: 12px;
    }
`;

const ErrorText = styled.div`
    margin-top: 8px;
    margin-bottom: 12px;
    font-size: 13px;
    color: red;
`;

const ContinueButton = styled(Button)`
    width: 100%;
    border: none;
    padding: 8px 0px;
    ${(p) =>
        !p.hasError &&
        css`
            margin-top: 24px;
        `}

    &:hover,
    &:active {
        background-color: ${yellow60};
        border: none;
        color: ${blue100};
        cursor: pointer;
    }
`;

export default withCustomMetrics({
    section: 'Interim',
    component: 'NewExisting',
    category: 'Booking - Interim',
    initiator: EVENT_INITIATORS.USER,
})(NewOrExistingVerification);
